import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { DEFECTS } from "../../api/query";
import { useEffect, useMemo, useState } from "react";
import Loader from "../../components/common/Loader";
import DynamicTable from "../../components/common/DynamicTable";
import { format, parse } from "date-fns";
import { useNavigate } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import BreadCumb from "../../components/common/Breadcrumb";
import backArrow from "../../assets/task/back_arrow.svg";
import { UPDATE_DEFECT } from "../../api/mutation";
import { useAuth } from "../../context/AuthContext";
import { showError, showSuccess } from "../../components/common/ToastNotification";
import Popup from "../../components/dashboard/Popup";
import NotifyPopup from "../../components/dashboard/NotifyPopup";

const ViewPreDefects = () => {
    const navigate = useNavigate();
    const { userInfo } = useAuth()

    const employeeID = userInfo?.employee_id;

    //constants
    const [sorting, setSorting] = useState<SortingState>([]);
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
    const [mainTable, setMainTable] = useState<any>(null);
    const [mainTableUpdate, setMainTableUpdate] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState<number>(0);

    const [predefectPopup, setPreDefectPopup] = useState(false)
    const [defect, setDefect] = useState<any>(null);

    //api
    const [getDefects, { data: defectsData, loading: defectsDataLoading }] = useLazyQuery(DEFECTS, {
        fetchPolicy: 'no-cache'
    })
    const [updateDefect, { loading: updateDefectLoading }] = useMutation(UPDATE_DEFECT, {
        fetchPolicy: 'no-cache',
    })

    //useEffect
    useEffect(() => {
        getDefects({
            variables: {
                page: 1,
                page_size: 8,
                is_confirmed: false
            }
        })
    }, [])

    // Use useMemo to optimize the column generation
    const columns: any[] = [
        {
            key: "title",
            name: "Title",
            id: "title",
            header: "Title",
            accessorKey: "title",
            enableSorting: true,
            sortingFn: (rowA: any, rowB: any, columnId: any) => {
                const valueA = String(
                    rowA.getValue(columnId) || ""
                ).toLowerCase();
                const valueB = String(
                    rowB.getValue(columnId) || ""
                ).toLowerCase();
                return valueA.localeCompare(valueB);
            },
        },
        {
            key: "description",
            name: "Description",
            id: "description",
            header: "Description",
            accessorKey: "description",
            enableSorting: true,
            sortingFn: (rowA: any, rowB: any, columnId: any) => {
                const valueA = String(
                    rowA.getValue(columnId) || ""
                ).toLowerCase();
                const valueB = String(
                    rowB.getValue(columnId) || ""
                ).toLowerCase();
                return valueA.localeCompare(valueB);
            },
        },
        // {
        //     key: "severity",
        //     name: "Severity",
        //     id: "severity",
        //     header: "Severity",
        //     accessorKey: "severity",
        //     enableSorting: true,
        //     sortingFn: (rowA: any, rowB: any, columnId: any) => {
        //         const valueA = String(
        //             rowA.getValue(columnId) || ""
        //         ).toLowerCase();
        //         const valueB = String(
        //             rowB.getValue(columnId) || ""
        //         ).toLowerCase();
        //         return valueA.localeCompare(valueB);
        //     },
        // },
        {
            key: "created_on",
            name: "Created On",
            id: "created_on",
            header: "Created On",
            accessorKey: "created_on",
            enableSorting: true,
            sortingFn: (rowA: any, rowB: any) => {
                const startDateA = rowA?.getValue("created_on");
                const startDateB = rowB?.getValue("created_on");

                // If a start_date is missing (null or undefined), treat it as the latest date.
                // You can also treat missing dates as the earliest date by using `new Date(0)` instead of `new Date()`
                const defaultDate = new Date(0); // You can change this to `new Date()` if you want it sorted to the end.

                const dateA = startDateA ? parse(startDateA, "MM-dd-yyyy", new Date()) : defaultDate;
                const dateB = startDateB ? parse(startDateB, "MM-dd-yyyy", new Date()) : defaultDate;

                // Ensure valid date parsing, otherwise fallback to defaultDate
                if (isNaN(dateA.getTime())) {
                    dateA.setTime(defaultDate.getTime());
                }
                if (isNaN(dateB.getTime())) {
                    dateB.setTime(defaultDate.getTime());
                }

                // Compare the full dates directly (ascending or descending will be handled by react-table)
                return dateA.getTime() - dateB.getTime();
            },
        },
        {
            key: "action",
            name: "Action",
            id: "action",
            header: "Action",
            accessorKey: "action",
            hideSearch: true,
            cell: ({ row }: any) => {
                return (
                    <div className="flex items-center gap-[8px]  theme_btn_item">
                        <button
                            className="dash-action-link cursor-pointer dash-action"
                            onClick={() => {
                                navigate("/view_defect", {
                                    state: {
                                        defectID: row.original.id,
                                    }
                                })
                            }}
                        >
                            <span className="text-sm dark:text-white  btn-col">YES</span>
                        </button>
                        <button
                            className="dash-action-link cursor-pointer dash-actions  "
                            onClick={() => {
                                setPreDefectPopup(true);
                                setDefect(row.original);
                            }}
                        >
                            <span className="text-sm dark:text-white btn-cols ">NO</span>
                        </button>
                    </div >
                );
            },
        },
    ]

    async function handleNoDefectClick() {
        const input = {
            title: defect?.title,
            description: defect?.description,
            steps_to_reproduce: defect?.stepsToReproduce,
            expected_behavior: defect?.expectedBehaviour,
            actual_behavior: defect?.actualBehaviour,
            attachment_list: defect?.attachmentList,
            severity: defect?.severity,
            created_by: defect?.createdBy,
            is_issue: false,
            is_confirmed: true,
            confirmed_by: parseInt(employeeID)
        }

        try {
            const response = await updateDefect({ variables: { id: defect?.id, input: input } });
            if (response.data.update_defect.status_code === 200) {
                showSuccess(response.data.update_defect.message)

                await getDefects({
                    variables: {
                        page: 1,
                        page_size: 8,
                        is_confirmed: false
                    }
                })
            }
            if (response.data.update_defect.status_code === 400) {
                showError(response.data.update_defect.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }

    const formattedData = useMemo(() => {
        if (defectsData && defectsData.defects.status_code === 200) {
            setTotalPages(defectsData?.defects?.pagination.total_pages)
            const defects = defectsData.defects.defects || [];
            return defects.map((d: any) => {
                const attachmentList = d.attachments_list?.map((attachment: any, index: number) => {
                    return {
                        file_name: attachment.file_name,  // Use fileNames if defined
                        file_url: attachment.file_url,
                        uploaded_by: parseInt(employeeID), // Convert to integer
                    };
                }) || [];

                return {
                    title: d.title,
                    severity: d.severity,
                    created_on: format(d?.created_on, 'MM-dd-yyyy'),
                    id: d.id,
                    description: d.description,
                    stepsToReproduce: d?.steps_to_reproduce,
                    expectedBehaviour: d?.expected_behavior,
                    actualBehaviour: d?.actual_behavior,
                    attachmentList: attachmentList,
                    createdBy: d.created_by,
                    isIssue: false,
                    isConfirmed: true,
                    confirmedBy: d?.confirmed_by,
                };
            });
        }
        else {
            return [];
        }
    }, [defectsData]);

    function paginationhandle(pageNo: number, itemsPerPage: number) {
        setCurrentPage(pageNo);

        getDefects({
            variables: {
                page: pageNo,
                page_size: 8,
                is_confirmed: false
            }
        })
    }

    async function handlePopupClose() {
        setPreDefectPopup(false)
        await getDefects({
            variables: {
                page: 1,
                page_size: 8,
                is_confirmed: false
            }
        })
    }

    return (
        <>
            <div id="cts-respon-p-dv" className="w-full  relative  overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4  box-border text-left text-base text-gray font-manrope">
                <div className="flex-1 flex flex-col items-start justify-start gap-6">
                    <BreadCumb />

                    <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
                        <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" alt="" src={backArrow}
                            onClick={() => navigate(-1)}
                        />
                        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">Pre Defects</h3>
                    </div>
                    {(formattedData?.length > 0) ? (
                        <DynamicTable
                            filteredData={formattedData}
                            selectedColumns={columns}
                            sorting={sorting}
                            setSorting={setSorting}
                            selectedRows={selectedRows}
                            setSelectedRows={setSelectedRows}
                            setMainTable={setMainTable}
                            setMainTableUpdate={setMainTableUpdate}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            handlePageChange={paginationhandle}
                            showPagination={true}
                            dottedPagination={true}
                        />
                    ) : (
                        <div className="self-stretch rounded-lg  overflow-hidden rounded-lg flex flex-col items-center justify-center py-[20px] px-[20px] gap-[8px] text-center text-text-text-secondary mq450:pl-5 mq450:pr-5 mq450:box-border">
                            <div className="self-stretch bg-white rounded-lg bg-slate-100 overflow-hidden dark:bg-slate-700 flex flex-col items-center justify-center py-[110px] px-[53px] gap-[8px] text-center text-text-text-secondary mq450:pl-5 mq450:pr-5 mq450:box-border">
                                {/* <img
                            className="w-9 h-9 relative overflow-hidden shrink-0"
                            alt=""
                            // src={alertOrAction === "action" ? firsflag1 : iioniconaalertcircleoutline}
                        /> */}
                                <p className="relative my-0">No data available</p>
                            </div>
                        </div>
                    )}

                    {(defectsDataLoading || updateDefectLoading) && (
                        <Loader />
                    )}
                </div>
            </div>
            {predefectPopup && (
                <NotifyPopup
                    onsubmit={handleNoDefectClick}
                    back={() => {
                        setPreDefectPopup(false);
                    }}
                    isDelete={true}
                    showMessage={"Are you sure you want to delete this record?"}
                />
            )}
        </>
    )
}

export default ViewPreDefects; 